import React, { useContext, useState, useEffect } from "react";
import styled from "styled-components";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import confetti from "canvas-confetti";
import {
  Card,
  Table,
  Button,
  Tag,
  Statistic,
  Progress,
  Row,
  Col,
  DatePicker,
  Select,
  message,
  Tooltip,
  Badge,
  Modal,
  Form,
  Input,
  InputNumber,
  Empty,
  Skeleton,
  Alert,
} from "antd";
import {
  DollarOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  ExclamationCircleOutlined,
  CloseCircleOutlined,
  RollbackOutlined,
  PlusOutlined,
  DownloadOutlined,
  EyeOutlined,
  MessageOutlined,
  UserOutlined,
  HomeOutlined,
  InfoCircleOutlined,
  BellOutlined,
  CreditCardOutlined,
  SettingOutlined,
  FileAddOutlined,
} from "@ant-design/icons";
import { AuthContext } from "../context/AuthProvider";
import {
  getPaymentsByOwner,
  getPaymentStats,
  createRentPayment,
  markPaymentAsPaid,
  markPaymentAsOverdue,
  markPaymentAsPending,
  generateMonthlyPayments,
  getAllPayments,
  deleteRentPayment,
} from "../lib/firebase/paymentModel";
import { sendReminder } from "../lib/firebase/rentModel";
import {
  listTenants,
  updateTenant,
  createTenant,
} from "../lib/firebase/tenantModel";
import unitModel from "../lib/firebase/unitModel";
import { generateTenantReminders } from "../lib/firebase/reminderModel";
import { device, LAYOUT_CONSTANTS } from "../lib/breakpoints";
import { Spacer } from "../styles/CommonElements";
import PaymentDetailsSection from "../components/rent/PaymentDetailsSection";
import RecordPaymentModal from "../components/rent/RecordPaymentModal";
import PageHeader from "../components/PageHeader";
import {
  StatCard,
  SectionCard,
  QuickActionButton,
  QuickActionLink,
  EmptyState,
  DetailsModal,
} from "../components/shared";
import CollectionProgress from "../components/rent/CollectionProgress";
import { propertyKeys } from "../lib/queryKeys";
import { trackEvent } from "../helpers/mixpanel";
import { useNavigate } from "react-router-dom";
import SettingsModel from "../lib/firebase/settingsModel";
import { useConnectStatus } from "../hooks/useConnectStatus";
import { getDebugMode, getDebugUnitCount } from "../helpers/constants";
import {
  getDebugPayments,
  getDebugTenants,
  getDebugUnitsExtended,
} from "../helpers/debugData";
import { getUnitDisplayAddress } from "../lib/unitAddressUtils";
import { shouldShowScaleUpgradeForFullOs } from "../helpers/accountProduct";
import ScaleUpgradeModal from "../components/ScaleUpgradeModal";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;
const { Item } = Form;

const RentDashboard = () => {
  const { user, userInfo } = useContext(AuthContext);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [dateRange, setDateRange] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selectedUnit, setSelectedUnit] = useState("all");

  // Wrapper functions with tracking
  const handleStatusFilterChange = (value) => {
    trackEvent("payment_filter_status_change", { status: value });
    setStatusFilter(value);
  };

  const handleUnitFilterChange = (value) => {
    trackEvent("payment_filter_unit_change", { unitId: value });
    setSelectedUnit(value);
  };

  const handleDateRangeChange = (dates) => {
    trackEvent("payment_filter_date_range_change", {
      hasStartDate: !!dates?.[0],
      hasEndDate: !!dates?.[1],
    });
    setDateRange(dates);
  };
  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isTenantModalVisible, setIsTenantModalVisible] = useState(false);
  const [isUnitModalVisible, setIsUnitModalVisible] = useState(false);
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedUnitForModal, setSelectedUnitForModal] = useState(null);
  const [sendingReminderToPaymentId, setSendingReminderToPaymentId] =
    useState(null);
  const [isGeneratingPayments, setIsGeneratingPayments] = useState(false);
  const [isMarkPaidModalVisible, setIsMarkPaidModalVisible] = useState(false);
  const [paymentToMarkPaid, setPaymentToMarkPaid] = useState(null);
  const [isMarkingPaid, setIsMarkingPaid] = useState(false);
  const [scaleUpgradeModalOpen, setScaleUpgradeModalOpen] = useState(false);
  const [form] = Form.useForm();
  const [tenantForm] = Form.useForm();
  const [unitForm] = Form.useForm();

  // Debug mode - automatically determined by route
  const isDebugMode = getDebugMode();
  const debugUnitCount = getDebugUnitCount();

  // Load data using useQuery for better caching and performance
  const { data: paymentsResult, isLoading: isPaymentsLoading } = useQuery({
    queryKey: propertyKeys.payments(user?.uid, isDebugMode, debugUnitCount),
    queryFn: async () => {
      if (isDebugMode) {
        const debugPayments = getDebugPayments(debugUnitCount);
        return Promise.resolve({
          success: true,
          payments: debugPayments.payments,
        });
      }
      console.log("[RentDashboard] Fetching payments for user:", user?.uid);
      const result = await getPaymentsByOwner();
      console.log("[RentDashboard] Payments result:", result);
      try {
        trackEvent("payments_fetch", { count: result?.payments?.length || 0 });
      } catch (error) {
        console.warn("[RentDashboard] Failed to track payments_fetch:", error);
      }
      return result;
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: statsResult, isLoading: isStatsLoading } = useQuery({
    queryKey: propertyKeys.paymentStats(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        const debugPayments = getDebugPayments(debugUnitCount);
        return Promise.resolve({ success: true, stats: debugPayments.summary });
      }
      return getPaymentStats();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: tenantsResult, isLoading: isTenantsLoading } = useQuery({
    queryKey: propertyKeys.tenants(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        // Pass unitCount so tenants match the occupied units (85% occupancy)
        return Promise.resolve({ tenants: getDebugTenants(debugUnitCount) });
      }
      return listTenants();
    },
    enabled: !!user?.uid || isDebugMode,
  });

  const { data: units = [], isLoading: isUnitsLoading } = useQuery({
    queryKey: propertyKeys.units(user?.uid, isDebugMode, debugUnitCount),
    queryFn: () => {
      if (isDebugMode) {
        return Promise.resolve(getDebugUnitsExtended(debugUnitCount));
      }
      return unitModel.getByOwner(user.uid);
    },
    enabled: !!user?.uid || isDebugMode,
  });

  // Load settings to check payment setup status
  const { data: userSettings } = useQuery({
    queryKey: propertyKeys.settings(user?.uid),
    queryFn: () => SettingsModel.getOrCreateSettings(),
    enabled: !!user?.uid,
  });

  // Check Connect status for real-time payment capability
  const { canAcceptPayments, loading: connectStatusLoading } = useConnectStatus(
    user?.uid
  );

  const payments = paymentsResult?.success ? paymentsResult.payments || [] : [];
  const stats = statsResult?.success
    ? statsResult.stats
    : {
        totalRent: 0,
        collectedRent: 0,
        pendingRent: 0,
        processingRent: 0,
        overdueRent: 0,
        collectionRate: 0,
        totalUnits: 0,
        occupiedUnits: 0,
      };
  const tenants = tenantsResult?.tenants || [];
  const loading =
    isPaymentsLoading || isStatsLoading || isTenantsLoading || isUnitsLoading;

  // Final total for a payment (base amount + sum of adjustments) so stats and totals respect credits/adjustments
  const getPaymentFinalTotal = (payment) => {
    const base = payment?.amount || 0;
    const adjustments = payment?.adjustments || [];
    const adjustmentSum = adjustments.reduce(
      (sum, adj) => sum + (adj.amount || 0),
      0
    );
    return base + adjustmentSum;
  };

  // Process payments to automatically mark overdue payments
  const processedPayments = payments.map((payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day for comparison

    // If payment is pending and due date is before today, mark as overdue
    if (payment.status === "pending" && dueDate < today) {
      return {
        ...payment,
        status: "overdue",
      };
    }
    return payment;
  });

  // Update overdue payments in database
  useEffect(() => {
    const updateOverduePayments = async () => {
      const overduePayments = processedPayments.filter((payment) => {
        const originalPayment = payments.find((p) => p.id === payment.id);
        return (
          originalPayment &&
          originalPayment.status === "pending" &&
          payment.status === "overdue"
        );
      });

      for (const payment of overduePayments) {
        try {
          await markPaymentAsOverdue(payment.id);
          console.log(
            `[RentDashboard] Updated payment ${payment.id} to overdue`
          );
        } catch (error) {
          console.error(
            `[RentDashboard] Failed to update payment ${payment.id} to overdue:`,
            error
          );
        }
      }
    };

    if (payments.length > 0) {
      updateOverduePayments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payments]); // processedPayments is derived from payments, so we only need payments in deps

  // Calculate statistics from processed payments
  const pendingPayments = payments.filter(
    (payment) => payment.status === "pending"
  );

  // Get payments for the selected month (for totalRent calculation)
  const selectedMonthPayments = processedPayments.filter((payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    const targetMonth = selectedMonth.getMonth();
    const targetYear = selectedMonth.getFullYear();
    return (
      dueDate.getMonth() === targetMonth && dueDate.getFullYear() === targetYear
    );
  });

  // Count as paid: status 'paid' or 'succeeded' (Stripe sets succeeded when payment completes)
  const isPaid = (p) => {
    const s = (p?.status || "").toLowerCase();
    return s === "paid" || s === "succeeded";
  };
  const selectedMonthPaidPayments = selectedMonthPayments.filter(isPaid);
  const selectedMonthPendingPayments = selectedMonthPayments.filter(
    (payment) => (payment.status || "").toLowerCase() === "pending"
  );
  const selectedMonthOverduePayments = selectedMonthPayments.filter(
    (payment) => (payment.status || "").toLowerCase() === "overdue"
  );
  const selectedMonthProcessingPayments = selectedMonthPayments.filter(
    (payment) => (payment.status || "").toLowerCase() === "processing"
  );

  const calculatedStats = {
    // Only include payments for the selected month; use final total (base + adjustments)
    totalRent: selectedMonthPayments.reduce(
      (sum, payment) => sum + getPaymentFinalTotal(payment),
      0
    ),
    collectedRent: selectedMonthPaidPayments.reduce(
      (sum, payment) => sum + getPaymentFinalTotal(payment),
      0
    ),
    pendingRent: selectedMonthPendingPayments.reduce(
      (sum, payment) => sum + getPaymentFinalTotal(payment),
      0
    ),
    overdueRent: selectedMonthOverduePayments.reduce(
      (sum, payment) => sum + getPaymentFinalTotal(payment),
      0
    ),
    processingRent: selectedMonthProcessingPayments.reduce(
      (sum, payment) => sum + getPaymentFinalTotal(payment),
      0
    ),
    collectionRate: 0, // Will be calculated separately for this month
    totalUnits: units.length,
    occupiedUnits: tenants.length,
  };

  // Get month name for a given date
  const getMonthName = (date) => {
    const dueDate = date?.toDate?.() || new Date(date);
    return dueDate.toLocaleDateString("en-US", { month: "long" });
  };

  // Format dates like "Jan 2, 2026"
  const formatShortDate = (date) => {
    if (!date) return "N/A";
    const d = date?.toDate?.() || new Date(date);
    if (Number.isNaN(d.getTime())) return "N/A";
    return d.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  // Check if a payment is for next month
  const isNextMonthPayment = (payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return (
      dueDate.getMonth() === nextMonth.getMonth() &&
      dueDate.getFullYear() === nextMonth.getFullYear()
    );
  };

  // Check if a payment is for current month
  const isCurrentMonthPayment = (payment) => {
    const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
    const now = new Date();
    return (
      dueDate.getMonth() === now.getMonth() &&
      dueDate.getFullYear() === now.getFullYear()
    );
  };

  // Calculate collection rate for selected month
  const getCurrentMonthCollectionRate = () => {
    // Get payments due in the selected month
    const currentMonthPayments = getPaymentsForMonth(selectedMonth);

    if (currentMonthPayments.length === 0) {
      return 0;
    }

    // Calculate percentage of paid payments for this month (include 'succeeded' from Stripe)
    const paidPayments = currentMonthPayments.filter((p) => {
      const s = (p?.status || "").toLowerCase();
      return s === "paid" || s === "succeeded";
    });
    return Math.round(
      (paidPayments.length / currentMonthPayments.length) * 100
    );
  };

  // Get payments for a specific month
  const getPaymentsForMonth = (month) => {
    const targetMonth = month.getMonth();
    const targetYear = month.getFullYear();

    return processedPayments.filter((payment) => {
      const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
      return (
        dueDate.getMonth() === targetMonth &&
        dueDate.getFullYear() === targetYear
      );
    });
  };

  // Get current month payments for collection progress
  const getCurrentMonthPayments = () => {
    return getPaymentsForMonth(selectedMonth);
  };

  // Get months that have payments for navigation
  const getMonthsWithPayments = () => {
    if (!processedPayments || processedPayments.length === 0) {
      return [];
    }

    const monthSet = new Set();
    processedPayments.forEach((payment) => {
      const dueDate = payment.dueDate?.toDate?.() || new Date(payment.dueDate);
      const monthKey = `${dueDate.getFullYear()}-${dueDate.getMonth()}`;
      monthSet.add(monthKey);
    });

    return Array.from(monthSet)
      .map((monthKey) => {
        const [year, month] = monthKey.split("-");
        return new Date(parseInt(year), parseInt(month), 1);
      })
      .sort((a, b) => a - b);
  };

  // Handle month change for collection progress
  const handleMonthChange = (newMonth) => {
    setSelectedMonth(newMonth);
  };

  // Get current month name
  const getCurrentMonthName = () => {
    const now = new Date();
    return now.toLocaleDateString("en-US", { month: "long" });
  };

  // Use calculated stats if no stats from API, otherwise use API stats
  const finalStats = statsResult?.success ? stats : calculatedStats;

  // Override all stats to use selected month payments only; use final total (base + adjustments)
  finalStats.totalRent = selectedMonthPayments.reduce(
    (sum, payment) => sum + getPaymentFinalTotal(payment),
    0
  );
  finalStats.collectedRent = selectedMonthPaidPayments.reduce(
    (sum, payment) => sum + getPaymentFinalTotal(payment),
    0
  );
  finalStats.pendingRent = selectedMonthPendingPayments.reduce(
    (sum, payment) => sum + getPaymentFinalTotal(payment),
    0
  );
  finalStats.overdueRent = selectedMonthOverduePayments.reduce(
    (sum, payment) => sum + getPaymentFinalTotal(payment),
    0
  );
  finalStats.processingRent = selectedMonthProcessingPayments.reduce(
    (sum, payment) => sum + getPaymentFinalTotal(payment),
    0
  );

  // Override collection rate with current month calculation
  finalStats.collectionRate = getCurrentMonthCollectionRate();

  // Debug logging
  console.log("[RentDashboard] Current state:", {
    user: user?.uid,
    paymentsCount: payments.length,
    processedPaymentsCount: processedPayments.length,
    paymentsResult,
    finalStats,
    tenantsCount: tenants.length,
    unitsCount: units.length,
    loading,
    currentMonthCollectionRate: getCurrentMonthCollectionRate(),
  });

  const getStatusColor = (status) => {
    switch (status) {
      // Green: succeeded, paid
      case "succeeded":
      case "paid":
        return "success";
      // Blue: pending, pending_verification, processing, created
      case "pending":
      case "pending_verification":
      case "processing":
      case "created":
        return "processing"; // Use 'processing' instead of 'warning' to avoid Ant Design's yellow
      // Red: failed, canceled, overdue
      case "failed":
      case "canceled":
      case "overdue":
        return "error";
      // Purple: refunded
      case "refunded":
        return "default";
      default:
        return "default";
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      // Success statuses
      case "succeeded":
      case "paid":
        return <CheckCircleOutlined />;
      // Pending/processing statuses
      case "pending":
      case "processing":
      case "created":
        return <ClockCircleOutlined />;
      // Verification required
      case "pending_verification":
        return <ExclamationCircleOutlined />;
      // Failed/error statuses
      case "failed":
      case "overdue":
        return <ExclamationCircleOutlined />;
      // Canceled
      case "canceled":
        return <CloseCircleOutlined />;
      // Refunded
      case "refunded":
        return <RollbackOutlined />;
      default:
        return <ClockCircleOutlined />;
    }
  };

  const columns = [
    {
      title: "Tenant",
      dataIndex: "tenantId",
      key: "tenantId",
      render: (tenantId) => {
        const tenant = tenants.find((t) => t.id === tenantId);
        if (!tenant) return "Unknown Tenant";

        const tenantName = tenant.name || "Unknown Tenant";
        const tenantContent = (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "6px",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            onClick={() => {
              setSelectedTenant(tenant);
              setIsTenantModalVisible(true);
            }}
          >
            <UserOutlined style={{ color: "var(--color-primary)" }} />
            <span style={{ color: "var(--color-primary)", fontWeight: "500" }}>
              {tenantName}
            </span>
          </div>
        );

        return (
          <Tooltip
            title={`Tenant: ${tenantName}${
              tenant.email ? ` (${tenant.email})` : ""
            }`}
          >
            {tenantContent}
          </Tooltip>
        );
      },
    },
    {
      title: "Unit",
      dataIndex: "unitId",
      key: "unitId",
      render: (unitId) => {
        const unit = units.find((u) => u.id === unitId);
        if (!unit) return "Unknown door";

        const unitContent = (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              cursor: "pointer",
              padding: "4px 8px",
              borderRadius: "6px",
              transition: "background-color 0.2s ease",
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = "#f0f0f0")}
            onMouseLeave={(e) =>
              (e.target.style.backgroundColor = "transparent")
            }
            onClick={() => {
              setSelectedUnitForModal(unit);
              setIsUnitModalVisible(true);
            }}
          >
            <HomeOutlined style={{ color: "var(--color-primary)" }} />
            <span style={{ color: "var(--color-primary)", fontWeight: "500" }}>
              {unit.name}
            </span>
          </div>
        );

        return (
          <Tooltip
            title={`Door: ${unit.name}${
              unit.monthlyRent
                ? ` (Rent: $${
                    typeof unit.monthlyRent === "number"
                      ? unit.monthlyRent.toLocaleString()
                      : unit.monthlyRent
                  })`
                : ""
            }`}
          >
            {unitContent}
          </Tooltip>
        );
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount, record) => {
        const formattedAmount = `$${amount?.toLocaleString() || "0"}`;
        const isOneOff = record.isOneOff === true;
        const paymentType = record.type || "rent";

        // Get payment type label
        const typeLabels = {
          rent: "Rent",
          utility: "Utility",
          late_fee: "Late Fee",
          security_deposit: "Security Deposit",
          other: "Other",
        };
        const typeLabel = typeLabels[paymentType] || "Payment";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              flexWrap: "wrap",
            }}
          >
            <Tooltip title={`${typeLabel} amount: ${formattedAmount}`}>
              <span>{formattedAmount}</span>
            </Tooltip>
            {/* One-off tag hidden for now */}
            {false && isOneOff && (
              <Tag color="orange" size="small" style={{ margin: 0 }}>
                One-off
              </Tag>
            )}
            {paymentType !== "rent" && (
              <Tag color="blue" size="small" style={{ margin: 0 }}>
                {typeLabel}
              </Tag>
            )}
          </div>
        );
      },
    },
    {
      title: "Due Date",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (date) => {
        if (!date) return "N/A";
        const dueDate = date?.toDate?.() || new Date(date);
        const formattedDate = formatShortDate(dueDate);
        return (
          <Tooltip title={`Payment due date: ${formattedDate}`}>
            <span>{formattedDate}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        // Map status to display label
        const statusLabels = {
          created: "Created",
          pending: "Pending",
          pending_verification: "Verification Required",
          processing: "Processing",
          succeeded: "Paid",
          paid: "Paid",
          failed: "Failed",
          canceled: "Canceled",
          refunded: "Refunded",
          overdue: "Overdue", // Legacy status
        };

        // Official backend status descriptions
        const statusDescriptions = {
          created:
            "Payment record created. Awaiting tenant to initiate payment.",
          pending: "Payment has not been received yet. Awaiting tenant to pay.",
          pending_verification:
            "Bank account verification required. Please complete microdeposits verification to proceed.",
          processing:
            "Payment is being processed. Tenant has initiated payment. This may take 1-3 business days for ACH payments.",
          succeeded: "Payment has been successfully processed and confirmed.",
          paid: "Payment has been successfully processed and confirmed.",
          failed:
            "Payment failed. Please reach out to your tenant to try the payment again.",
          canceled: "Payment was canceled.",
          refunded: "Payment was refunded.",
          overdue:
            "Payment is past the due date and has not been received. Consider sending a reminder.",
        };

        const statusText =
          statusLabels[status] ||
          status.charAt(0).toUpperCase() + status.slice(1);

        // Build tooltip text with additional context
        let tooltipText =
          statusDescriptions[status] || `Payment status: ${statusText}`;

        // Add error message for failed payments
        if (status === "failed" && record.lastPaymentError?.message) {
          tooltipText = `Payment failed: ${record.lastPaymentError.message}`;
        }

        // Add verification URL hint for pending_verification
        if (status === "pending_verification" && record.hostedVerificationUrl) {
          tooltipText += " Click to complete verification.";
        }

        // Add month label for pending payments
        let monthLabel = "";
        if (status === "pending") {
          if (isNextMonthPayment(record)) {
            monthLabel = ` (${getMonthName(record.dueDate)})`;
          } else if (isCurrentMonthPayment(record)) {
            monthLabel = ` (${getMonthName(record.dueDate)})`;
          }
        }

        return (
          <Tooltip title={tooltipText} trigger={["hover", "click"]}>
            <span style={{ cursor: "help" }}>
              <StyledStatusTag
                color={getStatusColor(status)}
                icon={getStatusIcon(status)}
              >
                {statusText}
                {monthLabel}
              </StyledStatusTag>
            </span>
          </Tooltip>
        );
      },
    },
    {
      title: "Payment Method",
      dataIndex: "method",
      key: "method",
      render: (method) => {
        if (!method) return "-";

        // Capitalize the first letter and handle common payment methods
        const capitalized =
          method.charAt(0).toUpperCase() + method.slice(1).toLowerCase();

        // Handle specific payment method abbreviations
        let displayMethod = capitalized;
        if (method.toLowerCase() === "ach") displayMethod = "ACH";
        else if (method.toLowerCase() === "check") displayMethod = "Check";
        else if (method.toLowerCase() === "cash") displayMethod = "Cash";
        else if (method.toLowerCase() === "credit") displayMethod = "Credit";
        else if (method.toLowerCase() === "debit") displayMethod = "Debit";
        else if (method.toLowerCase() === "wire") displayMethod = "Wire";
        else if (method.toLowerCase() === "zelle") displayMethod = "Zelle";
        else if (method.toLowerCase() === "venmo") displayMethod = "Venmo";
        else if (method.toLowerCase() === "paypal") displayMethod = "PayPal";

        return (
          <Tooltip title={`Payment method: ${displayMethod}`}>
            <span>{displayMethod}</span>
          </Tooltip>
        );
      },
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        console.log("[DEBUG] Rendering actions for record:", record);
        return (
          <Button.Group size="small">
            <Tooltip title="View Details">
              <Button icon={<EyeOutlined />} />
            </Tooltip>
            {(record.status === "pending" || record.status === "overdue") && (
              <Tooltip title="Mark as Paid">
                <Button
                  icon={
                    <CheckCircleOutlined
                      style={{ filter: "brightness(0) invert(1) !important" }}
                    />
                  }
                  onClick={() => handleMarkPaid(record.id)}
                />
              </Tooltip>
            )}
            {record.status !== "paid" && (
              <Tooltip title="Send Reminder">
                <Button
                  icon={<MessageOutlined />}
                  loading={sendingReminderToPaymentId === record.id}
                  disabled={sendingReminderToPaymentId === record.id}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    console.log(
                      "[DEBUG] Send reminder button clicked for payment:",
                      record.id
                    );
                    // Prevent double-clicks by checking if already sending
                    if (sendingReminderToPaymentId !== record.id) {
                      handleSendReminder(record.id);
                    }
                  }}
                />
              </Tooltip>
            )}
          </Button.Group>
        );
      },
    },
  ];

  const paymentMatchesStatusFilter = (payment, filter) => {
    if (filter === "all") return true;
    const s = (payment.status || "").toLowerCase();
    if (filter === "pending") {
      return (
        s === "pending" ||
        s === "processing" ||
        s === "pending_verification" ||
        s === "created"
      );
    }
    if (filter === "processing") {
      return s === "processing";
    }
    return s === filter;
  };

  const processingPaymentsAll = processedPayments.filter(
    (p) => (p.status || "").toLowerCase() === "processing"
  );

  const filteredPayments = processedPayments
    .filter((payment) => {
      if (!paymentMatchesStatusFilter(payment, statusFilter)) return false;

      // Filter by unit
      if (selectedUnit !== "all") {
        const unit = units.find((u) => u.id === selectedUnit);
        if (!unit || payment.unitId !== selectedUnit) return false;
      }

      // Filter by date range
      if (dateRange && dateRange.length === 2) {
        const paymentDate =
          payment.dueDate?.toDate?.() || new Date(payment.dueDate);
        const startDate = dateRange[0];
        const endDate = dateRange[1];

        // Ensure dates are proper Date objects
        const startDateObj =
          startDate instanceof Date ? startDate : new Date(startDate);
        const endDateObj =
          endDate instanceof Date ? endDate : new Date(endDate);

        // Reset time to start of day for accurate comparison
        const paymentDateOnly = new Date(
          paymentDate.getFullYear(),
          paymentDate.getMonth(),
          paymentDate.getDate()
        );
        const startDateOnly = new Date(
          startDateObj.getFullYear(),
          startDateObj.getMonth(),
          startDateObj.getDate()
        );
        const endDateOnly = new Date(
          endDateObj.getFullYear(),
          endDateObj.getMonth(),
          endDateObj.getDate()
        );

        if (paymentDateOnly < startDateOnly || paymentDateOnly > endDateOnly) {
          return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      const dateA = a.dueDate?.toDate?.() || new Date(a.dueDate);
      const dateB = b.dueDate?.toDate?.() || new Date(b.dueDate);

      // First sort by date (descending - most recent dates first)
      const dateComparison = dateB.getTime() - dateA.getTime();

      // If dates are the same, sort by status priority
      if (dateComparison === 0) {
        const statusRank = (st) => {
          const x = (st || "").toLowerCase();
          if (x === "overdue" || x === "failed") return 1;
          if (x === "processing" || x === "pending_verification") return 2;
          if (x === "pending" || x === "created") return 3;
          if (x === "paid" || x === "succeeded") return 4;
          return 5;
        };
        return statusRank(a.status) - statusRank(b.status);
      }

      return dateComparison;
    });

  const handleRecordPayment = async (values) => {
    try {
      console.log("[RentDashboard] Creating payment with values:", values);
      try {
        trackEvent("payment_create_submit", {
          amount: values.amount,
          method: values.paymentMethod,
        });
      } catch (error) {
        console.warn(
          "[RentDashboard] Failed to track payment_create_submit:",
          error
        );
      }
      // Determine payment type and isOneOff flag
      const paymentType = values.paymentType || "rent";
      // One-off payments are any payment type other than recurring rent
      const isOneOff = paymentType !== "rent";

      // Map paymentType to the type field used in the payment model
      const typeMapping = {
        rent: "rent",
        one_off_rent: "rent",
        utility: "utility",
        late_fee: "late_fee",
        security_deposit: "security_deposit",
        other: "other",
      };

      const result = await createRentPayment({
        tenantId: values.tenantId,
        unitId: values.unitId,
        amount: values.amount,
        dueDate: values.dueDate?.toDate?.() || values.dueDate,
        method: values.paymentMethod,
        notes: values.notes,
        type: typeMapping[paymentType] || "rent",
        isOneOff: isOneOff, // Always false for now
        status: values.status || "pending",
      });

      console.log("[RentDashboard] Payment creation result:", result);

      if (result.success) {
        message.success("Payment recorded successfully!");
        try {
          trackEvent("payment_create_success", { id: result.id });
        } catch (error) {
          console.warn(
            "[RentDashboard] Failed to track payment_create_success:",
            error
          );
        }
        setIsModalVisible(false);
        form.resetFields();
        // Invalidate and refetch payments data
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
      } else {
        message.error(result.error || "Failed to record payment");
        try {
          trackEvent("payment_create_error", { error: result.error });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_create_error:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error recording payment:", error);
      message.error("Failed to record payment");
      try {
        trackEvent("payment_create_exception", { message: String(error) });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_create_exception:",
          err
        );
      }
    }
  };

  const triggerConfetti = () => {
    // LandlordOps brand colors
    const colors = ["#86E49D", "#A4F1B1", "#5DD77A", "#2C5F2D"];

    const duration = 2000; // 2 seconds (faster)
    const end = Date.now() + duration;

    const confettiInterval = setInterval(() => {
      if (Date.now() > end) {
        clearInterval(confettiInterval);
        return;
      }

      // Left side - angled to cross middle
      confetti({
        particleCount: 10,
        angle: 75,
        spread: 80,
        origin: { x: 0, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
        drift: 0.5, // Drift towards center
      });

      // Right side - angled to cross middle
      confetti({
        particleCount: 10,
        angle: 105,
        spread: 80,
        origin: { x: 1, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
        drift: -0.5, // Drift towards center
      });

      // Top center - wide spread
      confetti({
        particleCount: 15,
        angle: 90,
        spread: 90,
        origin: { x: 0.5, y: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });

      // Additional bursts from sides for more coverage
      confetti({
        particleCount: 8,
        angle: 60,
        spread: 70,
        origin: { x: 0 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });

      confetti({
        particleCount: 8,
        angle: 120,
        spread: 70,
        origin: { x: 1 },
        colors: colors,
        gravity: 0.5,
        scalar: 1.5,
      });
    }, 100); // Faster interval - every 100ms instead of 250ms
  };

  const handleMarkPaid = (paymentId) => {
    // Find the payment to show in confirmation modal
    const payment = processedPayments.find((p) => p.id === paymentId);
    if (payment) {
      setPaymentToMarkPaid(payment);
      setIsMarkPaidModalVisible(true);
      try {
        trackEvent("payment_mark_paid_click", { paymentId });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_paid_click:",
          err
        );
      }
    }
  };

  const confirmMarkPaid = async () => {
    if (!paymentToMarkPaid || isMarkingPaid) return;

    const paymentId = paymentToMarkPaid.id;
    setIsMarkingPaid(true);
    try {
      const result = await markPaymentAsPaid(paymentId, "manual");
      if (result.success) {
        message.success("Payment marked as paid!");
        triggerConfetti(); // Trigger confetti celebration
        try {
          trackEvent("payment_mark_paid_success", { paymentId });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_paid_success:",
            err
          );
        }
        // Invalidate and refetch payments data
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
        setIsMarkPaidModalVisible(false);
        setPaymentToMarkPaid(null);
      } else {
        message.error(result.error || "Failed to mark payment as paid");
        try {
          trackEvent("payment_mark_paid_error", {
            paymentId,
            error: result.error,
          });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_paid_error:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error marking payment as paid:", error);
      message.error("Failed to mark payment as paid");
      try {
        trackEvent("payment_mark_paid_exception", {
          paymentId,
          message: String(error),
        });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_paid_exception:",
          err
        );
      }
    } finally {
      setIsMarkingPaid(false);
    }
  };

  const cancelMarkPaid = () => {
    if (isMarkingPaid) return;
    setIsMarkPaidModalVisible(false);
    setPaymentToMarkPaid(null);
  };

  const handleMarkOverdue = async (paymentId) => {
    try {
      try {
        trackEvent("payment_mark_overdue_click", { paymentId });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_overdue_click:",
          err
        );
      }
      const result = await markPaymentAsOverdue(paymentId);
      if (result.success) {
        message.success("Payment marked as overdue!");
        try {
          trackEvent("payment_mark_overdue_success", { paymentId });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_overdue_success:",
            err
          );
        }
        // Invalidate and refetch payments data
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
      } else {
        message.error(result.error || "Failed to mark payment as overdue");
        try {
          trackEvent("payment_mark_overdue_error", {
            paymentId,
            error: result.error,
          });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_overdue_error:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error marking payment as overdue:", error);
      message.error("Failed to mark payment as overdue");
      try {
        trackEvent("payment_mark_overdue_exception", {
          paymentId,
          message: String(error),
        });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_overdue_exception:",
          err
        );
      }
    }
  };

  const handleMarkPending = async (paymentId) => {
    try {
      try {
        trackEvent("payment_mark_pending_click", { paymentId });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_pending_click:",
          err
        );
      }
      const result = await markPaymentAsPending(paymentId);
      if (result.success) {
        message.success("Payment marked as pending!");
        try {
          trackEvent("payment_mark_pending_success", { paymentId });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_pending_success:",
            err
          );
        }
        // Invalidate and refetch payments data
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
      } else {
        message.error(result.error || "Failed to mark payment as pending");
        try {
          trackEvent("payment_mark_pending_error", {
            paymentId,
            error: result.error,
          });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payment_mark_pending_error:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error marking payment as pending:", error);
      message.error("Failed to mark payment as pending");
      try {
        trackEvent("payment_mark_pending_exception", {
          paymentId,
          message: String(error),
        });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payment_mark_pending_exception:",
          err
        );
      }
    }
  };

  const handleDeletePayment = async (paymentId) => {
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      content: (
        <div
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
          }}
        >
          <div
            style={{
              textAlign: "center",
              marginBottom: "1.5rem",
              padding: "1.5rem",
            }}
          >
            <h3
              style={{
                fontFamily: "var(--font-primary)",
                fontSize: "1.125rem",
                fontWeight: 700,
                color: "var(--color-text)",
                margin: "0 0 0.5rem 0",
                lineHeight: "1.4",
              }}
            >
              Are you sure you want to delete this payment?
            </h3>
          </div>

          <div
            style={{
              background: "rgba(239, 68, 68, 0.08)",
              padding: "1rem 1.25rem",
              borderRadius: "8px",
              border: "1px solid rgba(239, 68, 68, 0.15)",
              display: "flex",
              alignItems: "center",
              gap: "0.75rem",
            }}
          >
            <span style={{ fontSize: "1.25rem", flexShrink: 0 }}>⚠️</span>
            <p
              style={{
                color: "var(--color-error)",
                fontWeight: 600,
                margin: 0,
                lineHeight: "1.5",
                fontSize: "0.9375rem",
                flex: 1,
              }}
            >
              This action cannot be undone. The payment will be permanently
              deleted.
            </p>
          </div>
        </div>
      ),
      okText: "Delete Payment",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          try {
            trackEvent("payment_delete_click", { paymentId });
          } catch (err) {
            console.warn(
              "[RentDashboard] Failed to track payment_delete_click:",
              err
            );
          }
          const result = await deleteRentPayment(paymentId);
          if (result.success) {
            message.success("Payment deleted successfully!");
            try {
              trackEvent("payment_delete_success", { paymentId });
            } catch (err) {
              console.warn(
                "[RentDashboard] Failed to track payment_delete_success:",
                err
              );
            }
            // Invalidate and refetch payments data
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.payments(user?.uid),
            });
            await queryClient.invalidateQueries({
              queryKey: propertyKeys.paymentStats(user?.uid),
            });
          } else {
            message.error(result.error || "Failed to delete payment");
            try {
              trackEvent("payment_delete_error", {
                paymentId,
                error: result.error,
              });
            } catch (err) {
              console.warn(
                "[RentDashboard] Failed to track payment_delete_error:",
                err
              );
            }
          }
        } catch (error) {
          console.error("Error deleting payment:", error);
          message.error("Failed to delete payment");
          try {
            trackEvent("payment_delete_exception", {
              paymentId,
              message: String(error),
            });
          } catch (err) {
            console.warn(
              "[RentDashboard] Failed to track payment_delete_exception:",
              err
            );
          }
        }
      },
    });
  };

  const handleGenerateMonthlyPayments = async () => {
    setIsGeneratingPayments(true);
    try {
      try {
        trackEvent("payments_generate_monthly_click");
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payments_generate_monthly_click:",
          err
        );
      }
      const result = await generateMonthlyPayments();
      if (result.success) {
        message.success("Monthly payments generated successfully!");
        try {
          trackEvent("payments_generate_monthly_success", {
            count: result?.generated || 0,
          });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payments_generate_monthly_success:",
            err
          );
        }
        // Invalidate and refetch payments data
        await queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
      } else {
        message.error(result.error || "Failed to generate monthly payments");
        try {
          trackEvent("payments_generate_monthly_error", {
            error: result.error,
          });
        } catch (err) {
          console.warn(
            "[RentDashboard] Failed to track payments_generate_monthly_error:",
            err
          );
        }
      }
    } catch (error) {
      console.error("Error generating monthly payments:", error);
      message.error("Failed to generate monthly payments");
      try {
        trackEvent("payments_generate_monthly_exception", {
          message: String(error),
        });
      } catch (err) {
        console.warn(
          "[RentDashboard] Failed to track payments_generate_monthly_exception:",
          err
        );
      }
    } finally {
      setIsGeneratingPayments(false);
    }
  };

  const handleDebugAllPayments = async () => {
    try {
      console.log("[RentDashboard] Debug: Checking all payments...");
      const result = await getAllPayments();
      console.log("[RentDashboard] Debug result:", result);
      if (result.success) {
        message.success(
          `Found ${result.payments.length} total payments in collection`
        );
      } else {
        message.error(result.error || "Failed to get all payments");
      }
    } catch (error) {
      console.error("Error getting all payments:", error);
      message.error("Failed to get all payments");
    }
  };

  const handleCreateTestPayments = async () => {
    try {
      console.log("[RentDashboard] Creating test payments...");

      if (tenants.length === 0 || units.length === 0) {
        message.error("Please create at least one tenant and door first");
        return;
      }

      const testPayments = [
        {
          tenantId: tenants[0].id,
          unitId: units[0].id,
          amount: 1500,
          dueDate: new Date(),
          method: "ach",
          notes: "Test recurring rent payment",
          type: "rent",
          isOneOff: false,
          status: "pending",
        },
        {
          tenantId: tenants[0].id,
          unitId: units[0].id,
          amount: 1500,
          dueDate: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000), // 30 days ago
          method: "check",
          notes: "Test recurring rent payment (paid)",
          type: "rent",
          isOneOff: false,
          status: "paid",
        },
        // One-off payments hidden for now
        // {
        //   tenantId: tenants[0].id,
        //   unitId: units[0].id,
        //   amount: 250,
        //   dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000), // 7 days from now
        //   method: 'ach',
        //   notes: 'One-off utility payment for water heater repair',
        //   type: 'utility',
        //   isOneOff: true,
        //   status: 'pending'
        // },
        // {
        //   tenantId: tenants.length > 1 ? tenants[1].id : tenants[0].id,
        //   unitId: units.length > 1 ? units[1].id : units[0].id,
        //   amount: 500,
        //   dueDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000), // 14 days from now
        //   method: 'credit_card',
        //   notes: 'One-off rent payment for additional month',
        //   type: 'rent',
        //   isOneOff: true,
        //   status: 'pending'
        // },
        // {
        //   tenantId: tenants[0].id,
        //   unitId: units[0].id,
        //   amount: 75,
        //   dueDate: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000), // 5 days ago
        //   method: 'ach',
        //   notes: 'One-off late fee payment',
        //   type: 'late_fee',
        //   isOneOff: true,
        //   status: 'paid'
        // }
      ];

      for (const paymentData of testPayments) {
        const result = await createRentPayment(paymentData);
        console.log("[RentDashboard] Test payment result:", result);
        if (!result.success) {
          message.error(`Failed to create test payment: ${result.error}`);
          return;
        }
      }

      message.success("Test payments created successfully!");
    } catch (error) {
      console.error("Error creating test payments:", error);
      message.error("Failed to create test payments");
    }
  };

  const handleGenerateReminders = async () => {
    try {
      const result = await generateTenantReminders();
      if (result.success) {
        message.success(
          `Generated ${result.reminders.length} reminders successfully!`
        );
      } else {
        message.error(result.error || "Failed to generate reminders");
      }
    } catch (error) {
      console.error("Error generating reminders:", error);
      message.error("Failed to generate reminders");
    }
  };

  const handleSendReminder = (paymentId) => {
    console.log("[DEBUG] handleSendReminder called with paymentId:", paymentId);

    // Prevent duplicate calls - if already sending for this payment, ignore
    if (sendingReminderToPaymentId === paymentId) {
      console.log(
        "[DEBUG] Already sending reminder for this payment, ignoring duplicate call"
      );
      return;
    }

    // Find the payment to get tenant information
    const payment = paymentsResult?.payments?.find((p) => p.id === paymentId);
    console.log("[DEBUG] Found payment:", payment);

    if (!payment) {
      console.log("[DEBUG] Payment not found");
      message.error("Payment not found");
      return;
    }

    // Find the tenant
    const tenant = tenantsResult?.tenants?.find(
      (t) => t.id === payment.tenantId
    );
    console.log("[DEBUG] Found tenant:", tenant);

    if (!tenant) {
      console.log("[DEBUG] Tenant not found");
      message.error("Tenant not found");
      return;
    }

    // Find the unit
    const unit = units.find((u) => u.id === payment.unitId);
    const unitDisplay = unit
      ? `${getUnitDisplayAddress(unit) || unit.address || ""}${
          unit.unitNumber ? ` #${unit.unitNumber}` : ""
        }`.trim()
      : "";
    const unitName = unitDisplay || "Unknown door";
    const tenantName =
      tenant.firstName && tenant.lastName
        ? `${tenant.firstName} ${tenant.lastName}`
        : tenant.name || tenant.displayName || "Unknown Tenant";
    const amount = `$${getPaymentFinalTotal(payment).toFixed(2)}`;
    const dueDate = payment.dueDate ? formatShortDate(payment.dueDate) : "N/A";
    const paymentStatus = payment.status || "pending";
    const statusText =
      paymentStatus.charAt(0).toUpperCase() + paymentStatus.slice(1);

    // Get month name for the title
    const monthName = payment.dueDate
      ? getMonthName(payment.dueDate)
      : new Date().toLocaleDateString("en-US", { month: "long" });

    // Show confirmation modal
    Modal.confirm({
      title: null,
      icon: null,
      centered: true,
      maskClosable: true,
      content: (
        <div
          className="payment-reminder-modal-content"
          style={{
            fontFamily: "var(--font-secondary)",
            color: "var(--color-text)",
          }}
        >
          <div
            className="payment-reminder-header"
            style={{
              textAlign: "center",
              marginBottom: "1.5rem",
              padding: "1.5rem",
            }}
          >
            <h3
              style={{
                fontFamily: "var(--font-primary)",
                fontSize: "1.125rem",
                fontWeight: 700,
                color: "var(--color-text)",
                margin: 0,
                lineHeight: "1.4",
              }}
            >
              Send {monthName} Rent Reminder?
            </h3>
          </div>

          <div
            className="payment-reminder-details"
            style={{
              background: "rgba(44, 95, 45, 0.08)",
              padding: "1rem 1.25rem",
              borderRadius: "8px",
              border: "1px solid rgba(44, 95, 45, 0.15)",
              marginBottom: "1rem",
            }}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "0.5rem",
              }}
            >
              <div
                className="payment-detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    color: "var(--color-text-light)",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  Tenant:
                </span>
                <span
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textAlign: "right",
                    wordBreak: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {tenantName}
                </span>
              </div>
              <div
                className="payment-detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    color: "var(--color-text-light)",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  Unit:
                </span>
                <span
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textAlign: "right",
                    wordBreak: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {unitName}
                </span>
              </div>
              <div
                className="payment-detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    color: "var(--color-text-light)",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  Amount:
                </span>
                <span
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textAlign: "right",
                    wordBreak: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {amount}
                </span>
              </div>
              <div
                className="payment-detail-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    color: "var(--color-text-light)",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  Due Date:
                </span>
                <span
                  style={{
                    color: "var(--color-text)",
                    fontSize: "0.9375rem",
                    fontWeight: 600,
                    textAlign: "right",
                    wordBreak: "break-word",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  {dueDate}
                </span>
              </div>
              <div
                className="payment-detail-row payment-status-row"
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: "0.5rem",
                }}
              >
                <span
                  style={{
                    color: "var(--color-text-light)",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                    flexShrink: 0,
                  }}
                >
                  Status:
                </span>
                <span
                  style={{
                    textAlign: "right",
                    flex: 1,
                    minWidth: 0,
                  }}
                >
                  <Tag
                    color={getStatusColor(paymentStatus)}
                    icon={getStatusIcon(paymentStatus)}
                  >
                    {statusText}
                  </Tag>
                </span>
              </div>
              <div
                style={{
                  marginTop: "0.5rem",
                  paddingTop: "0.5rem",
                  borderTop: "1px solid rgba(44, 95, 45, 0.1)",
                }}
              >
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: "var(--color-text-light)",
                    margin: 0,
                    lineHeight: "1.4",
                    fontStyle: "italic",
                  }}
                >
                  The tenant will receive an email with a link to pay this
                  specific payment.
                </p>
              </div>
            </div>
          </div>
        </div>
      ),
      okText: "Send Reminder",
      okType: "primary",
      cancelText: "Cancel",
      onOk: async () => {
        await sendReminderConfirmed(paymentId, payment, tenant);
      },
    });
  };

  const sendReminderConfirmed = async (paymentId, payment, tenant) => {
    // Set loading state
    setSendingReminderToPaymentId(paymentId);

    try {
      console.log(
        "[DEBUG] Calling sendReminder API with paymentId:",
        paymentId
      );
      // Call the sendReminder function with paymentId
      const result = await sendReminder(payment.tenantId, paymentId);
      console.log("[DEBUG] sendReminder result:", result);

      if (result.success) {
        console.log("[DEBUG] Reminder sent successfully");
        // Use tenant name from API response if available, fallback to local tenant data
        const tenantName =
          result.data?.tenantName || `${tenant.firstName} ${tenant.lastName}`;
        message.success(`Rent reminder sent to ${tenantName}`);

        // Track the event
        trackEvent("rent_reminder_sent", {
          payment_id: paymentId,
          tenant_id: payment.tenantId,
          tenant_name: tenantName,
          unit_id: payment.unitId,
          amount: payment.amount,
        });

        // Refresh the data
        queryClient.invalidateQueries({
          queryKey: propertyKeys.payments(user?.uid),
        });
      } else {
        console.log("[DEBUG] Reminder failed:", result);
        message.error("Failed to send reminder");
      }
    } catch (error) {
      console.error("[DEBUG] Error sending reminder:", error);
      // Display specific error messages
      if (
        error.message?.includes("Payment not found") ||
        error.message?.includes("does not belong")
      ) {
        message.error(error.message);
      } else {
        message.error("Failed to send reminder");
      }
    } finally {
      // Clear loading state
      setSendingReminderToPaymentId(null);
    }
  };

  // Helper function to render empty state
  const renderEmptyState = () => {
    const hasTenants = tenants.length > 0;
    const hasUnits = units.length > 0;

    if (!hasTenants && !hasUnits) {
      return (
        <EmptyState
          title="No Rent Data Available"
          subtitle="To see rent payments here, you need to set up your property portfolio."
          steps={[
            "Add doors to your property portfolio",
            "Assign tenants to those units",
            "Set up rent schedules OR record manual payments (pending payments are created automatically within 30 days of their due date)",
          ]}
        />
      );
    }

    if (!hasTenants) {
      return (
        <EmptyState
          icon={<UserOutlined />}
          title="No Tenants Assigned"
          subtitle="You have units but no tenants assigned. Let's get your properties occupied!"
          steps={[
            "Go to the Tenants page",
            "Add tenants and assign them to units",
            "Set monthly rent amounts",
            "Pending payments are created automatically within 30 days of their due date",
          ]}
          actions={[
            <QuickActionButton
              key="manual-payment"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              Record Manual Payment
            </QuickActionButton>,
          ]}
        />
      );
    }

    if (!hasUnits) {
      return (
        <EmptyState
          icon={<HomeOutlined />}
          title="No Doors in Portfolio"
          subtitle="You have tenants but no doors. Let's set up your property portfolio!"
          steps={[
            "Go to the Doors page",
            "Add doors to your property portfolio",
            "Assign tenants to those doors",
            "Set up rent amounts",
            "Pending payments are created automatically within 30 days of their due date",
          ]}
          actions={[
            <QuickActionButton
              key="manual-payment"
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setIsModalVisible(true)}
            >
              Record Manual Payment
            </QuickActionButton>,
          ]}
        />
      );
    }

    return (
      <>
        <EmptyState
          title="No Rent Payments Yet"
          subtitle={
            <>
              <span className="desktop-only">
                Great! You have doors and tenants set up.
                <br />
                Now let's start tracking your rent payments.
              </span>
              <span className="mobile-only">
                You're all set up! Let's start tracking rent payments.
              </span>
            </>
          }
          steps={[
            "Use the 'Add Payment' button in the header to record manual payments",
            "Or click 'Generate Monthly Payments' below to create payments automatically",
            "Upcoming rent payments are created automatically each morning for tenants with active leases, if they aren't already recorded",
          ]}
          actions={[
            <GeneratePaymentsAction key="generate-payments">
              <QuickActionButton
                type="primary"
                icon={<FileAddOutlined />}
                onClick={handleGenerateMonthlyPayments}
                loading={isGeneratingPayments}
                disabled={isGeneratingPayments}
              >
                {isGeneratingPayments
                  ? "Generating..."
                  : "Generate Monthly Payments"}
              </QuickActionButton>
              <AutoGenerateNote $inline>
                <strong>Heads up:</strong> We automatically create the next
                upcoming rent payment each morning for tenants with an active
                lease, a rent amount, and a rent due day. Use the button above
                only to manually create the next upcoming payment or for
                testing. Upcoming payments are created automatically.
              </AutoGenerateNote>
            </GeneratePaymentsAction>,
          ]}
        />
      </>
    );
  };

  const needsScaleGate =
    !isDebugMode && shouldShowScaleUpgradeForFullOs(userInfo);

  if (needsScaleGate) {
    return (
      <Container className="rent-dashboard-container">
        <PageHeader
          icon={DollarOutlined}
          title="Payments"
          subtitle="Need more room to grow?"
        />
        <Card style={{ maxWidth: 560, margin: "2rem auto" }}>
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span>
                Need more room to grow? Upgrade your door cap via Billing to
                manage more units seamlessly with your full operational
                workspace.
              </span>
            }
          >
            <Button
              type="primary"
              onClick={() => setScaleUpgradeModalOpen(true)}
            >
              Why Scale?
            </Button>
          </Empty>
        </Card>
        <ScaleUpgradeModal
          open={scaleUpgradeModalOpen}
          onClose={() => setScaleUpgradeModalOpen(false)}
        />
      </Container>
    );
  }

  if (loading) {
    return (
      <Container className="rent-dashboard-container">
        <PageHeader
          icon={DollarOutlined}
          title="Payments"
          subtitle="Monitor and manage all payments including rent, fees, and deposits"
        />
        <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
          {[1, 2, 3, 4].map((i) => (
            <Col xs={24} sm={12} md={6} key={i}>
              <Card>
                <Skeleton active paragraph={{ rows: 2 }} />
              </Card>
            </Col>
          ))}
        </Row>
        <Card>
          <Skeleton active paragraph={{ rows: 8 }} />
        </Card>
      </Container>
    );
  }

  return (
    <Container className="rent-dashboard-container">
      <PageHeader
        title="Payments"
        subtitle="Monitor and manage all payments including rent, fees, and deposits"
        icon={DollarOutlined}
      />

      {/* Payment Setup Alert - Only show when loading is complete and payments are not set up */}
      {!connectStatusLoading && !canAcceptPayments && (
        <PaymentSetupAlert>
          <PaymentSetupIcon>
            <ExclamationCircleOutlined />
          </PaymentSetupIcon>
          <PaymentSetupContent>
            <PaymentSetupDescription>
              You need to configure payment processing to start collecting rent
              from tenants.{" "}
              <span>
                Set up your payment methods in Settings to enable automated rent
                collection.
              </span>
            </PaymentSetupDescription>
          </PaymentSetupContent>
          <PaymentSetupButton
            type="primary"
            size="small"
            icon={<SettingOutlined />}
            onClick={() => navigate("/settings#payment-setup")}
          >
            Set Up Payments
          </PaymentSetupButton>
        </PaymentSetupAlert>
      )}

      {/* Key Statistics: desktop (lg+) = five stat tiles in one row; smaller breakpoints stack/wrap */}
      <StatsGrid>
        <StatTile $due>
          <Tooltip
            title="Total amount of rent due from all active tenants for the current month"
            placement="top"
          >
            <StatCard
              title="Total Rent Due"
              value={finalStats.totalRent}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Tooltip>
        </StatTile>
        <StatTile>
          <Tooltip
            title="Total amount of rent that has been successfully collected from tenants across all time periods"
            placement="top"
          >
            <StatCard
              title="Collected"
              value={finalStats.collectedRent}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#3f8600" }}
            />
          </Tooltip>
        </StatTile>
        <StatTile>
          <Tooltip
            title="Rent payments that are scheduled but not yet due, including future month payments"
            placement="top"
          >
            <StatCard
              title="Pending"
              value={finalStats.pendingRent}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#faad14" }}
            />
          </Tooltip>
        </StatTile>
        <StatTile>
          <Tooltip
            title="Rent payments that are past their due date and have not been paid"
            placement="top"
          >
            <StatCard
              title="Overdue"
              value={finalStats.overdueRent}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#cf1322" }}
            />
          </Tooltip>
        </StatTile>
        <StatTile>
          <Tooltip
            title="Tenant initiated payment (e.g. ACH); Stripe is still clearing funds. Shown in the table with status Processing."
            placement="top"
          >
            <StatCard
              title="Processing"
              value={finalStats.processingRent ?? 0}
              prefix={<DollarOutlined />}
              valueStyle={{ color: "#1677ff" }}
            />
          </Tooltip>
        </StatTile>
      </StatsGrid>

      {processingPaymentsAll.length > 0 && (
        <Row style={{ marginBottom: "1.25rem" }}>
          <Col span={24}>
            <Alert
              type="info"
              showIcon
              icon={<ClockCircleOutlined />}
              message={`${processingPaymentsAll.length} payment${
                processingPaymentsAll.length === 1 ? "" : "s"
              } processing`}
              description={
                <>
                  About{" "}
                  <strong>
                    $
                    {processingPaymentsAll
                      .reduce(
                        (sum, p) => sum + getPaymentFinalTotal(p),
                        0
                      )
                      .toLocaleString("en-US", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                  </strong>{" "}
                  is clearing (ACH can take a few business days). These stay on
                  your payments list until Stripe marks them paid or failed.
                </>
              }
              action={
                <Button
                  size="small"
                  type="primary"
                  ghost
                  onClick={() => setStatusFilter("processing")}
                >
                  View processing only
                </Button>
              }
            />
          </Col>
        </Row>
      )}

      {/* Collection Progress and Quick Actions */}
      <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
        <Col xs={24}>
          <CollectionProgress
            collectionRate={finalStats.collectionRate}
            paidCount={
              getCurrentMonthPayments().filter((p) => {
                const s = (p?.status || "").toLowerCase();
                return s === "paid" || s === "succeeded";
              }).length
            }
            totalCount={getCurrentMonthPayments().length}
            onMonthChange={handleMonthChange}
            currentMonth={selectedMonth}
            monthsWithPayments={getMonthsWithPayments()}
          />
        </Col>
      </Row>

      <Spacer $marginTop="3rem" />

      {/* Debug Information */}
      {false && (
        <Row gutter={[24, 24]} style={{ marginBottom: "2rem" }}>
          <Col span={24}>
            <Card title="Debug Information" size="small">
              <Row gutter={[16, 16]}>
                <Col span={6}>
                  <strong>User ID:</strong> {user?.uid || "Not logged in"}
                </Col>
                <Col span={6}>
                  <strong>Payments:</strong> {payments.length}
                </Col>
                <Col span={6}>
                  <strong>Processed:</strong> {processedPayments.length}
                </Col>
                <Col span={6}>
                  <strong>Tenants:</strong> {tenants.length}
                </Col>
                <Col span={6}>
                  <strong>Doors:</strong> {units.length}
                </Col>
              </Row>
              <Row style={{ marginTop: "1rem" }}>
                <Col span={24}>
                  <strong>Payments Result:</strong>{" "}
                  {JSON.stringify(paymentsResult, null, 2)}
                </Col>
              </Row>
              <Row style={{ marginTop: "1rem" }}>
                <Col span={24}>
                  <strong>Final Stats:</strong>{" "}
                  {JSON.stringify(finalStats, null, 2)}
                </Col>
              </Row>
            </Card>
          </Col>
        </Row>
      )}

      {/* Filters and Table */}
      <PaymentDetailsSection
        payments={filteredPayments}
        totalPaymentsCount={processedPayments.length}
        units={units}
        tenants={tenants}
        loading={loading}
        statusFilter={statusFilter}
        selectedUnit={selectedUnit}
        onChangeStatusFilter={handleStatusFilterChange}
        onChangeUnitFilter={handleUnitFilterChange}
        onChangeDateRange={handleDateRangeChange}
        onMarkPaid={handleMarkPaid}
        onMarkOverdue={handleMarkOverdue}
        onMarkPending={handleMarkPending}
        onSendReminder={handleSendReminder}
        sendingReminderToPaymentId={sendingReminderToPaymentId}
        onDeletePayment={handleDeletePayment}
        renderEmptyState={renderEmptyState}
        onAddPayment={() => setIsModalVisible(true)}
        onRefresh={async () => {
          await queryClient.invalidateQueries({
            queryKey: propertyKeys.payments(user?.uid),
          });
        }}
      />

      {/* Record Payment Modal */}
      <RecordPaymentModal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleRecordPayment}
        tenants={tenants}
        units={units}
        form={form}
      />

      {/* Tenant Details Modal */}
      <DetailsModal
        open={isTenantModalVisible}
        onCancel={() => setIsTenantModalVisible(false)}
        title="Tenant Details"
        icon={<UserOutlined />}
        data={selectedTenant}
        type="tenant"
        isEditing={false}
        onSave={async (values) => {
          try {
            const result = await updateTenant(selectedTenant.id, values);
            if (result.success) {
              message.success("Tenant updated successfully!");
              setIsTenantModalVisible(false);
              // Invalidate and refetch data
              await queryClient.invalidateQueries({
                queryKey: propertyKeys.tenants(user?.uid),
              });
            } else {
              message.error(result.error || "Failed to update tenant");
            }
          } catch (error) {
            console.error("Error updating tenant:", error);
            message.error("Failed to update tenant");
          }
        }}
        relatedData={{ units }}
      />

      {/* Unit Details Modal */}
      <DetailsModal
        open={isUnitModalVisible}
        onCancel={() => setIsUnitModalVisible(false)}
        title="Unit Details"
        icon={<HomeOutlined />}
        data={selectedUnitForModal}
        type="unit"
        isEditing={false}
        onSave={async (values) => {
          try {
            const result = await unitModel.update(
              selectedUnitForModal.id,
              values
            );
            if (result) {
              message.success("Unit updated successfully!");
              setIsUnitModalVisible(false);
              // Invalidate and refetch data
              await queryClient.invalidateQueries({
                queryKey: propertyKeys.units(user?.uid),
              });
            } else {
              message.error("Failed to update unit");
            }
          } catch (error) {
            console.error("Error updating unit:", error);
            message.error("Failed to update unit");
          }
        }}
        relatedData={{ tenants }}
      />

      {/* Mark Paid Confirmation Modal */}
      <StyledConfirmModal
        title={
          <span>
            <span>Confirm Mark as Paid</span>
          </span>
        }
        open={isMarkPaidModalVisible}
        onCancel={cancelMarkPaid}
        onOk={confirmMarkPaid}
        okText="Mark Paid"
        cancelText="Cancel"
        confirmLoading={isMarkingPaid}
        closable={!isMarkingPaid}
        maskClosable={!isMarkingPaid}
        keyboard={!isMarkingPaid}
        cancelButtonProps={{ disabled: isMarkingPaid }}
        okButtonProps={{
          type: "primary",
          style: {
            background:
              "linear-gradient(135deg, var(--color-primary-darker) 0%, var(--color-primary-dark) 100%)",
            border: "none",
          },
        }}
        width={500}
        centered
      >
        {paymentToMarkPaid &&
          (() => {
            const tenant = tenants.find(
              (t) => t.id === paymentToMarkPaid.tenantId
            );
            const unit = units.find((u) => u.id === paymentToMarkPaid.unitId);
            const dueDate =
              paymentToMarkPaid.dueDate?.toDate?.() ||
              new Date(paymentToMarkPaid.dueDate);
            const formattedAmount = `$${(
              paymentToMarkPaid.amount || 0
            ).toLocaleString()}`;
            const formattedDate = formatShortDate(dueDate);

            // Get tenant full name
            const tenantName = tenant
              ? `${tenant.firstName || ""} ${tenant.lastName || ""}`.trim() ||
                tenant.name ||
                tenant.email ||
                "Unknown Tenant"
              : "Unknown Tenant";

            // Get unit address
            const unitAddress = unit
              ? getUnitDisplayAddress(unit) ||
                unit.name ||
                unit.unitNumber ||
                "Address not available"
              : "Address not available";

            return (
              <div style={{ padding: "8px 0" }}>
                <QuestionWrapper>
                  <p
                    style={{
                      margin: 0,
                      fontSize: "16px",
                      color: "var(--color-text)",
                    }}
                  >
                    Are you sure you want to mark this payment as paid?
                  </p>
                </QuestionWrapper>

                <div
                  style={{
                    background: "var(--color-bg-tertiary)",
                    borderRadius: "12px",
                    padding: "20px",
                    marginBottom: "20px",
                  }}
                >
                  <div style={{ marginBottom: "12px" }}>
                    <strong
                      style={{
                        color: "var(--color-text-light)",
                        fontSize: "13px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Tenant
                    </strong>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <UserOutlined
                        style={{ color: "var(--color-primary-darker)" }}
                      />
                      <span style={{ fontSize: "15px", fontWeight: 600 }}>
                        {tenantName}
                      </span>
                    </div>
                  </div>

                  {unit && (
                    <div style={{ marginBottom: "12px" }}>
                      <strong
                        style={{
                          color: "var(--color-text-light)",
                          fontSize: "13px",
                          display: "block",
                          marginBottom: "4px",
                        }}
                      >
                        Address
                      </strong>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                        }}
                      >
                        <HomeOutlined
                          style={{ color: "var(--color-primary-darker)" }}
                        />
                        <span style={{ fontSize: "15px" }}>{unitAddress}</span>
                      </div>
                    </div>
                  )}

                  <div style={{ marginBottom: "12px" }}>
                    <strong
                      style={{
                        color: "var(--color-text-light)",
                        fontSize: "13px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Amount
                    </strong>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <DollarOutlined
                        style={{ color: "var(--color-primary-darker)" }}
                      />
                      <span
                        style={{
                          fontSize: "18px",
                          fontWeight: 700,
                          color: "var(--color-primary-darker)",
                        }}
                      >
                        {formattedAmount}
                      </span>
                    </div>
                  </div>

                  <div>
                    <strong
                      style={{
                        color: "var(--color-text-light)",
                        fontSize: "13px",
                        display: "block",
                        marginBottom: "4px",
                      }}
                    >
                      Due Date
                    </strong>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                      }}
                    >
                      <CalendarOutlined
                        style={{ color: "var(--color-primary-darker)" }}
                      />
                      <span style={{ fontSize: "15px" }}>{formattedDate}</span>
                    </div>
                  </div>
                </div>

                <p
                  style={{
                    margin: "0",
                    fontSize: "14px",
                    color: "var(--color-text-light)",
                    fontStyle: "italic",
                    textAlign: "center",
                  }}
                >
                  This action cannot be undone. The payment will be marked as
                  paid and the status will be updated.
                </p>
              </div>
            );
          })()}
      </StyledConfirmModal>
    </Container>
  );
};

// Styled Components
const Container = styled.div`
  min-height: 100vh;
  background: var(--color-bg-primary);
  padding: 2rem;
  width: ${LAYOUT_CONSTANTS.DESKTOP_CONTENT_WIDTH};
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 1rem;
    width: 95vw;
    min-width: ${LAYOUT_CONSTANTS.MIN_CONTENT_WIDTH};
  }

  @media (min-width: 768px) and (max-width: 1200px) {
    padding: 1.5rem;
    width: 90vw;
  }

  /* Apply the new container class for better background */
  &.rent-dashboard-container {
    background: var(--color-bg-primary) !important;
  }

  .ant-modal-content {
    padding: 0px !important;
  }
`;

const StyledConfirmModal = styled(Modal)`
  --confirm-header-height: 56px;
  --confirm-close-size: 32px;
  --confirm-close-top: 12px;

  .ant-modal-header {
    background: linear-gradient(135deg, var(--color-primary) 0%, #4a7c59 100%);
    border-bottom: none;
    border-radius: 12px 12px 0 0;
    padding: 16px 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
    min-height: var(--confirm-header-height);
    box-sizing: border-box;
  }

  .ant-modal-title {
    color: white !important;
    font-weight: 600;
    font-size: 18px;
    line-height: 1;
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 0;
    width: 100%;
    justify-content: center;

    > span {
      display: flex;
      align-items: center;
      gap: 12px;
      line-height: 1;
    }

    .anticon {
      color: white !important;
      display: flex;
      align-items: center;
      justify-content: center;
      line-height: 1;
      font-size: 20px;
      flex-shrink: 0;
      margin: 0;
      padding: 0;
    }

    span span {
      color: white !important;
      display: inline-block;
      line-height: 1;
      margin: 0;
      padding: 0;
    }
  }

  .ant-modal-close {
    color: white !important;
    font-size: 16px;
    top: var(--confirm-close-top) !important;
    right: 20px;
    background: transparent !important;
    border-radius: 0 !important;
    width: var(--confirm-close-size) !important;
    height: var(--confirm-close-size) !important;
    padding: 0 !important;
    line-height: 1 !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    border: none !important;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;

    &:hover {
      color: white !important;
      background: transparent !important;
      border-color: transparent !important;
    }
  }

  .ant-modal-close .ant-modal-close-x,
  .ant-modal-close .ant-modal-close-icon,
  .ant-modal-close .anticon {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    width: 100% !important;
    height: 100% !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 1 !important;
    color: #ffffff !important;
  }

  .ant-modal-close svg {
    display: block;
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-close:hover .ant-modal-close-x,
  .ant-modal-close:hover .ant-modal-close-icon,
  .ant-modal-close:hover .anticon,
  .ant-modal-close:hover svg {
    color: #ffffff !important;
    fill: #ffffff !important;
  }

  .ant-modal-body {
    padding: 24px;
    background: #fafafa;
    overflow-x: hidden;
  }

  .ant-modal-footer {
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding: 16px 24px;
    margin-top: 24px;
    background: #fafafa;
  }

  .ant-modal-content {
    border-radius: 12px;
    overflow: hidden;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
    max-width: 100vw;
    padding: 0px;
  }

  .ant-modal {
    max-width: 90vw;
    margin: 20px auto;
  }

  /* Mobile optimizations */
  @media (max-width: 768px) {
    --confirm-header-height: 48px;
    --confirm-close-size: 28px;
    --confirm-close-top: 10px;

    .ant-modal {
      max-width: 95vw;
      margin: 10px auto;
    }

    .ant-modal-body {
      padding: 16px;
    }

    .ant-modal-header {
      padding: 12px 16px;
    }

    .ant-modal-title {
      font-size: 16px;
    }

    .ant-modal-close {
      right: 16px !important;
      font-size: 14px;
      position: absolute !important;
    }

    .ant-modal-footer {
      padding: 12px 16px;
      margin-top: 20px;
    }
  }

  @media (max-width: 480px) {
    .ant-modal {
      max-width: 98vw;
      margin: 5px auto;
    }

    .ant-modal-body {
      padding: 12px;
    }

    .ant-modal-header {
      padding: 10px 12px;
    }

    .ant-modal-title {
      font-size: 15px;
    }

    .ant-modal-footer {
      padding: 10px 12px;
      margin-top: 16px;
    }
  }
`;

const QuestionWrapper = styled.div`
  max-width: 400px;
  margin: 0 auto 20px auto;
  text-align: center;

  @media (max-width: 768px) {
    max-width: 280px;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  gap: 1.5rem;
  margin-bottom: 2rem;
  grid-template-columns: 1fr;

  .ant-card {
    --stat-title-size: 0.875rem;
    --stat-value-size: 1.5rem;
    --stat-suffix-size: 1.5rem;
    --stat-icon-size: 1.5rem;
  }

  .ant-statistic {
    text-align: center;
  }

  .ant-statistic-title {
    font-size: var(--stat-title-size) !important;
    line-height: 1.25;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .ant-statistic-content,
  .ant-statistic-content-value,
  .ant-statistic-content-value-int,
  .ant-statistic-content-value-decimal {
    font-size: var(--stat-value-size) !important;
    line-height: 1.1;
    font-weight: 700 !important;
  }

  .ant-statistic-content-suffix,
  .ant-statistic-content-prefix {
    font-size: var(--stat-suffix-size) !important;
    font-weight: 700 !important;
    line-height: 1.2;
  }

  .ant-statistic-content-prefix svg,
  .ant-statistic-content-prefix .anticon {
    font-size: var(--stat-icon-size) !important;
    width: var(--stat-icon-size) !important;
    height: var(--stat-icon-size) !important;
  }

  @media (max-width: 768px) {
    .ant-card {
      --stat-title-size: 0.8rem;
      --stat-value-size: 1.25rem;
      --stat-suffix-size: 1.25rem;
      --stat-icon-size: 1.25rem;
    }
  }

  @media (min-width: 576px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
`;

const StatTile = styled.div`
  min-width: 0;
  width: 100%;

  ${(p) =>
    p.$due
      ? `
    @media (min-width: 576px) and (max-width: 991.98px) {
      grid-column: 1 / -1;
      max-width: 420px;
      width: 100%;
      justify-self: center;
    }
  `
      : ""}
`;

const AutoGenerateNote = styled.div`
  max-width: 1000px;
  width: 100%;
  margin: ${({ $inline }) => ($inline ? "0" : "-1rem auto 2rem")};
  padding: 1rem 1.25rem;
  background: #fafafa;
  border: 1px dashed #cbd5f5;
  border-radius: 12px;
  font-family: var(--font-secondary);
  font-size: 0.95rem;
  color: #475569;
  line-height: 1.5;
  text-align: center;
  box-sizing: border-box;
  word-wrap: break-word;
  overflow-wrap: break-word;

  strong {
    color: #334155;
  }

  @media (min-width: 769px) {
    max-width: 1000px;
    padding: 1.25rem 1.75rem;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    padding: 1rem 0.875rem;
    font-size: 0.875rem;
    line-height: 1.55;
    margin-top: 0.5rem;
    margin-bottom: 0;
    border-radius: 10px;
  }
`;

const GeneratePaymentsAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  align-items: center;
  width: 100%;
  max-width: 1000px;
  box-sizing: border-box;

  @media (min-width: 769px) {
    max-width: 800px;
    margin: auto;
  }

  @media (max-width: 768px) {
    max-width: 100%;
    width: 100%;
    padding: 0;
    gap: 0.875rem;
    margin: 0;
  }
`;

// Payment Setup Alert Styled Components
const PaymentSetupAlert = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;
  background: linear-gradient(135deg, #fff8e1 0%, #fff3c4 100%);
  border: 2px solid #ffb74d;
  border-radius: 16px;
  padding: 1.5rem 2rem;
  margin-bottom: 2rem;
  box-shadow: 0 4px 12px rgba(255, 183, 77, 0.15);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #ff9800 0%, #ffb74d 50%, #ffc107 100%);
  }

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(255, 183, 77, 0.25);
    transition: all 0.3s ease;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
    padding: 1.5rem;
    margin-bottom: 1.5rem;

    &:hover {
      transform: none;
    }
  }
`;

const PaymentSetupContent = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  text-align: left;

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 100%;
    order: 2;
  }
`;

const PaymentSetupIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #ff9800 0%, #ffb74d 100%);
  border-radius: 50%;
  box-shadow: 0 3px 8px rgba(255, 152, 0, 0.3);
  flex-shrink: 0;

  .anticon {
    font-size: 24px;
    color: white;
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    order: 1;
    align-self: center;
    width: 40px;
    height: 40px;

    .anticon {
      font-size: 20px;
    }
  }
`;

const PaymentSetupDescription = styled.div`
  font-size: 0.95rem;
  color: #bf360c;
  line-height: 1.5;
  font-weight: 500;

  span {
    font-size: inherit;
  }

  @media (min-width: 769px) {
    span {
      display: block;
    }
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    font-size: 0.9rem;
    text-align: center;
    line-height: 1.4;
    margin-bottom: 1rem;
  }
`;

const PaymentSetupButton = styled(Button)`
  flex-shrink: 0;
  height: 44px;
  padding: 0 1.5rem;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 12px;
  background: linear-gradient(135deg, #2e7d32 0%, #388e3c 100%);
  border: none;
  box-shadow: 0 3px 8px rgba(46, 125, 50, 0.3);

  &:hover {
    background: linear-gradient(135deg, #1b5e20 0%, #2e7d32 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(46, 125, 50, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  .ant-btn-icon {
    filter: brightness(0) invert(1);
  }

  /* Mobile responsiveness */
  @media (max-width: 768px) {
    width: 100%;
    order: 3;
    height: 40px;
    font-size: 0.9rem;
    padding: 0 1rem;

    &:hover {
      transform: none;
    }
  }
`;

const StyledStatusTag = styled(Tag)`
  margin: 0;
  font-weight: 600;
  border-radius: 8px;
  padding: 0.35rem 0.85rem;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;

  // Blue: processing, pending, created, pending_verification
  &.ant-tag-processing,
  &.ant-tag-warning {
    // Ant Design uses 'warning' for yellow/orange, mapping to blue here
    background: #dbeafe !important; // Light blue
    background-color: #dbeafe !important; // Override inline styles
    border: 1px solid #60a5fa !important; // Medium blue
    border-color: #60a5fa !important; // Override inline styles
    color: #1e40af !important; // Dark blue

    .anticon {
      color: #2563eb !important; // Bright blue
    }
  }

  // Green: succeeded, paid
  &.ant-tag-success {
    background: #d1fae5 !important; // Light mint
    border: 1px solid #34d399 !important; // Bright green
    color: #065f46 !important; // Dark green
    font-weight: 600;

    .anticon {
      color: #059669 !important; // Emerald
    }
  }

  // Red: failed, canceled, overdue
  &.ant-tag-error {
    background: #dc2626 !important; // Dark red background for white text
    border: 1px solid #b91c1c !important; // Darker red border
    color: #ffffff !important; // White text

    .anticon {
      color: #ffffff !important; // White icon
    }
  }

  // Purple: refunded
  &.ant-tag-default {
    // Using default for refunded
    background: #f3e8ff !important; // Light purple
    border: 1px solid #c084fc !important; // Medium purple
    color: #6b21a8 !important; // Dark purple

    .anticon {
      color: #9333ea !important; // Bright purple
    }
  }
`;

export default RentDashboard;
